import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import Avatar from 'antd/lib/avatar';
import { antGrey } from '../styles/globalStyles';
import { Destination } from '@/types/destinations';
import { displayStringForDestination } from '@/helpers/displayHelpers';
import WorkflowsPlatformIcon from '../icons/WorkflowsPlatformIcon';

interface DestinationCounterProps {
    destinations: Destination[];
    size?: number;
    countOnly?: boolean;
    hideTooltip?: boolean;
    darkMode?: boolean;
}

export const DestinationCounter: React.FC<DestinationCounterProps> = ({
    destinations,
    size = 32,
    countOnly = false,
    hideTooltip = false,
    darkMode = false,
}) => {
    const count = destinations.length;

    const fontSizeRatio = 1 / 2; // based on figma design
    const fontSize = size * fontSizeRatio;

    const borderRadiusRatio = 4.5 / 20; // based on figma design
    const borderRadius = size * borderRadiusRatio;

    // Tooltip content - only computed when needed
    const tooltipContent =
        hideTooltip ? undefined : (
            <div style={{ maxHeight: '50rem', overflowY: 'auto' }}>
                <div>{`${count} destination${count !== 1 ? 's' : ''}:`}</div>
                {count > 0 && (
                    <ul style={{ paddingLeft: 16, marginBottom: 0, marginTop: 4 }}>
                        {destinations.map((destination) => (
                            <li key={destination.stepConfigId}>
                                {displayStringForDestination(
                                    destination.platform,
                                    destination.appStoreType,
                                    destination.name,
                                    destination.appName,
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );

    if (count === 0) {
        return null;
    } else if (count === 1 && !countOnly) {
        const destination = destinations[0];
        const letter = destination.name.charAt(0);

        return (
            <Tooltip title={tooltipContent}>
                <>
                    {!!destination.iconURL || !!letter ?
                        <Avatar
                            shape={'square'}
                            size={size}
                            src={destination.iconURL}
                            style={{
                                border: `${size * 0.042}px`,
                                borderRadius: `${size * 0.17}px`,
                            }}
                        >
                            {letter}
                        </Avatar>
                    :   <WorkflowsPlatformIcon platform={destination.platform} width={size} height={size} />}
                </>
            </Tooltip>
        );
    }

    const originalSizeToRoundedRectangleSizeRatio = 33.5 / 40; // based on figma design

    const isMoreThan99 = count > 99;
    return (
        <Tooltip title={tooltipContent}>
            <div
                style={{
                    position: 'relative',
                    display: 'block',
                    width: `${size}px`,
                    height: `${size}px`,
                    lineHeight: `${size}px`,
                    fontSize: fontSize,
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        width: `${size * originalSizeToRoundedRectangleSizeRatio}px`,
                        height: `${size * originalSizeToRoundedRectangleSizeRatio}px`,
                        lineHeight: `${size * originalSizeToRoundedRectangleSizeRatio - 2}px`,
                        borderRadius: borderRadius,
                        backgroundColor: darkMode ? antGrey.grey9 : antGrey.grey5,
                        border: darkMode ? `1px solid ${antGrey.grey7}` : '1px solid var(--rw-border-muted)',
                        top: 0,
                        right: 0,
                    }}
                />
                <div
                    style={{
                        position: 'absolute',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: `${size * originalSizeToRoundedRectangleSizeRatio}px`,
                        height: `${size * originalSizeToRoundedRectangleSizeRatio}px`,
                        lineHeight: `${size * originalSizeToRoundedRectangleSizeRatio - 2}px`,
                        borderRadius: borderRadius,
                        backgroundColor: darkMode ? antGrey.grey10 : antGrey.grey1,
                        border: darkMode ? `1px solid ${antGrey.grey7}` : '1px solid var(--rw-border-muted)',
                        bottom: 0,
                        left: 0,
                    }}
                >
                    <Text
                        style={{
                            fontSize: fontSize * (isMoreThan99 ? 0.6 : 1),
                            fontWeight: 600,
                            height: `${size * originalSizeToRoundedRectangleSizeRatio - 2}px`,
                            lineHeight: `${size * originalSizeToRoundedRectangleSizeRatio - 2}px`,
                            color: darkMode ? antGrey.grey1 : antGrey.grey7,
                        }}
                    >
                        {isMoreThan99 ? '99+' : count}
                    </Text>
                </div>
            </div>
        </Tooltip>
    );
};
